import { logWebRtcEvent } from './voiceWebRtcLog';

const DEFAULT_UNREGISTER_TIMEOUT_MS = 2500;

/**
 * Wait for JsSIP unregister (or transport death) before stop(), so FreeSWITCH drops the WSS contact.
 *
 * @param {import('jssip').UA|null|undefined} ua
 * @param {{ timeoutMs?: number, allContacts?: boolean }} [options]
 * @returns {Promise<'unregistered'|'disconnected'|'timeout'|'skipped'|'error'>}
 */
export function waitForUaUnregister(ua, options = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_UNREGISTER_TIMEOUT_MS;
  const allContacts = options.allContacts !== false;

  if (!ua) {
    return Promise.resolve('skipped');
  }

  const canSendUnregister = Boolean(
    ua.isConnected?.() && (ua.isRegistered?.() || typeof ua.unregister === 'function')
  );

  if (!canSendUnregister || !ua.isRegistered?.()) {
    return Promise.resolve('skipped');
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (reason) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        ua.off('unregistered', onUnregistered);
        ua.off('disconnected', onDisconnected);
      } catch {
        // ignore
      }
      resolve(reason);
    };

    const onUnregistered = () => finish('unregistered');
    const onDisconnected = () => finish('disconnected');
    const timer = setTimeout(() => finish('timeout'), timeoutMs);

    try {
      ua.on('unregistered', onUnregistered);
      ua.on('disconnected', onDisconnected);
      ua.unregister({ all: allContacts });
    } catch {
      finish('error');
    }
  });
}

/**
 * Unregister (when possible) then stop the UA so JsSIP auto-recovery cannot leave a zombie contact.
 *
 * @param {import('jssip').UA|null|undefined} ua
 * @param {{ timeoutMs?: number, reason?: string, allContacts?: boolean }} [options]
 * @returns {Promise<void>}
 */
export async function teardownSipUserAgent(ua, options = {}) {
  if (!ua) return;

  const reason = options.reason || 'teardown';
  const unregisterOutcome = await waitForUaUnregister(ua, {
    timeoutMs: options.timeoutMs,
    allContacts: options.allContacts
  });
  logWebRtcEvent('sdk.uaTeardown', { reason, unregisterOutcome });

  try {
    ua.stop();
  } catch {
    // already closed
  }
}
